import { useState, useEffect, useCallback } from 'react';
import { getFlaggedEntries, resolveEntry, getErrorMessage } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle, Clock, MapPin } from 'lucide-react';
import { format, parseISO } from 'date-fns';

export const FlaggedPage = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const response = await getFlaggedEntries();
      setEntries(response.data);
    } catch (error) {
      console.error('Failed to fetch flagged entries:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleResolve = async (id) => {
    try {
      await resolveEntry(id);
      toast.success('Entry resolved');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to resolve entry'));
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="flagged-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="flagged-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight font-['Manrope']">
          Flagged Entries
        </h1>
        <p className="text-muted-foreground mt-1">
          Review clock-ins that occurred outside geo-fence boundaries
        </p>
      </div>

      {/* Stats */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-amber-50 flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-amber-600" />
            </div>
            <div>
              <p className="text-4xl font-bold font-['Manrope']">{entries.length}</p>
              <p className="text-muted-foreground">Pending review</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Flagged Entries List */}
      <Card>
        <CardHeader>
          <CardTitle className="font-['Manrope']">All Flagged Entries</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {entries.length === 0 ? (
            <div className="empty-state py-12">
              <CheckCircle className="empty-state-icon text-emerald-500" />
              <p className="text-muted-foreground">No flagged entries!</p>
              <p className="text-sm text-muted-foreground mt-1">
                All clock-ins are within geo-fence boundaries
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {entries.map((entry) => (
                <div
                  key={entry.id}
                  className="p-6 hover:bg-muted/50 transition-colors"
                  data-testid={`flagged-entry-${entry.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                        <AlertTriangle className="w-6 h-6 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-semibold">{entry.user_name || 'Employee'}</p>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {format(parseISO(entry.clock_in), 'MMM d, yyyy h:mm a')}
                          </span>
                          {entry.clock_out && (
                            <span>
                              → {format(parseISO(entry.clock_out), 'h:mm a')}
                            </span>
                          )}
                        </div>
                        <div className="mt-2 p-2 rounded-lg bg-amber-50 text-amber-800 text-sm">
                          <MapPin className="w-3 h-3 inline mr-1" />
                          {entry.flag_reason}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {entry.total_hours && (
                        <Badge variant="outline">
                          {entry.total_hours.toFixed(2)} hrs
                        </Badge>
                      )}
                      <Button
                        onClick={() => handleResolve(entry.id)}
                        className="rounded-full"
                        data-testid={`resolve-entry-${entry.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Resolve
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
